import React from 'react';

type AudioContextConstructor = new () => AudioContext;
type RingtoneWindow = Window &
  typeof globalThis & {
    webkitAudioContext?: AudioContextConstructor;
  };

type ActiveRingtone = {
  context: AudioContext;
  intervalId: number;
  oscillators: Set<OscillatorNode>;
  closeOnStop: boolean;
};

let activeRingtone: ActiveRingtone | null = null;
let primedContext: AudioContext | null = null;

const ringIntervalMs = 1800;
const firstPulseOffsetSeconds = 0;
const secondPulseOffsetSeconds = 0.32;
const pulseLengthSeconds = 0.22;

const playPulse = (ringtone: ActiveRingtone, offsetSeconds: number) => {
  const oscillator = ringtone.context.createOscillator();
  const gain = ringtone.context.createGain();
  const startAt = ringtone.context.currentTime + offsetSeconds;
  const stopAt = startAt + pulseLengthSeconds;

  oscillator.type = 'sine';
  oscillator.frequency.setValueAtTime(760, startAt);
  gain.gain.setValueAtTime(0.0001, startAt);
  gain.gain.exponentialRampToValueAtTime(0.16, startAt + 0.03);
  gain.gain.exponentialRampToValueAtTime(0.0001, stopAt);

  oscillator.connect(gain);
  gain.connect(ringtone.context.destination);
  ringtone.oscillators.add(oscillator);

  oscillator.start(startAt);
  oscillator.stop(stopAt);

  window.setTimeout(
    () => {
      ringtone.oscillators.delete(oscillator);
      oscillator.disconnect();
      gain.disconnect();
    },
    (offsetSeconds + pulseLengthSeconds + 0.1) * 1000
  );
};

const playRingPattern = (ringtone: ActiveRingtone) => {
  playPulse(ringtone, firstPulseOffsetSeconds);
  playPulse(ringtone, secondPulseOffsetSeconds);
};

const getAudioContextConstructor = () => {
  if (typeof window === 'undefined') return null;
  const audioWindow = window as RingtoneWindow;
  return audioWindow.AudioContext ?? audioWindow.webkitAudioContext ?? null;
};

const resumeContext = (context: AudioContext) => {
  if (context.state === 'suspended') {
    void context.resume().catch(() => undefined);
  }
};

export const primeIncomingCallRingtone = () => {
  if (typeof window === 'undefined') return false;
  if (primedContext && primedContext.state !== 'closed') {
    resumeContext(primedContext);
    return true;
  }

  const AudioContextClass = getAudioContextConstructor();
  if (!AudioContextClass) return false;

  try {
    primedContext = new AudioContextClass();
    resumeContext(primedContext);
    return true;
  } catch {
    primedContext = null;
    return false;
  }
};

export const startIncomingCallRingtone = () => {
  if (activeRingtone || typeof window === 'undefined') return activeRingtone;

  const AudioContextClass = getAudioContextConstructor();
  if (!AudioContextClass) return null;

  try {
    const reusableContext =
      primedContext && primedContext.state !== 'closed' ? primedContext : null;
    const context = reusableContext ?? new AudioContextClass();
    const ringtone: ActiveRingtone = {
      context,
      intervalId: 0,
      oscillators: new Set(),
      closeOnStop: !reusableContext,
    };
    activeRingtone = ringtone;

    resumeContext(context);

    playRingPattern(ringtone);
    ringtone.intervalId = window.setInterval(() => {
      if (activeRingtone === ringtone) {
        playRingPattern(ringtone);
      }
    }, ringIntervalMs);

    return ringtone;
  } catch {
    activeRingtone = null;
    return null;
  }
};

export const stopIncomingCallRingtone = () => {
  if (!activeRingtone || typeof window === 'undefined') return;

  const ringtone = activeRingtone;
  activeRingtone = null;
  window.clearInterval(ringtone.intervalId);
  ringtone.oscillators.forEach(oscillator => {
    try {
      oscillator.stop();
    } catch {
      // Oscillators can already be stopped by their scheduled ring envelope.
    }
    oscillator.disconnect();
  });
  ringtone.oscillators.clear();
  if (ringtone.closeOnStop) {
    void ringtone.context.close().catch(() => undefined);
  }
};

export const usePrimeIncomingCallRingtone = (enabled: boolean) => {
  React.useEffect(() => {
    if (!enabled || typeof window === 'undefined') return undefined;

    const primeAndRemove = () => {
      if (!primeIncomingCallRingtone()) return;
      window.removeEventListener('pointerdown', primeAndRemove);
      window.removeEventListener('keydown', primeAndRemove);
      window.removeEventListener('touchstart', primeAndRemove);
    };

    window.addEventListener('pointerdown', primeAndRemove, { passive: true });
    window.addEventListener('keydown', primeAndRemove);
    window.addEventListener('touchstart', primeAndRemove, { passive: true });

    return () => {
      window.removeEventListener('pointerdown', primeAndRemove);
      window.removeEventListener('keydown', primeAndRemove);
      window.removeEventListener('touchstart', primeAndRemove);
    };
  }, [enabled]);
};

export const useIncomingCallRingtone = (enabled: boolean, callId: string | null | undefined) => {
  React.useEffect(() => {
    if (!enabled || !callId) {
      stopIncomingCallRingtone();
      return undefined;
    }

    startIncomingCallRingtone();
    return () => {
      stopIncomingCallRingtone();
    };
  }, [callId, enabled]);
};
